import React, { useState } from 'react';
import EventModal from '../ui/EventModal';
import Button from '../ui/Button';

/**
 * "Send this to the Melio team".
 *
 * THE CONFIRMATION MUST NOT OVERPROMISE. It says the conversation has been
 * flagged and that the team reviews these during working hours — it does not
 * say anyone has read it, and it does not promise a reply. That wording is the
 * one place a member is most likely to assume they have reached a person, so it
 * is also the place where an overpromise would do the most harm.
 *
 * Uses EventModal, the only generic modal in the member app.
 */

interface Props {
  onEscalate: (note?: string) => Promise<void>;
  disabled?: boolean;
  alreadyEscalated?: boolean;
}

export default function FeedbackEscalateButton({ onEscalate, disabled, alreadyEscalated }: Props) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onEscalate(note.trim() || undefined);
      setOpen(false);
      setNote('');
    } catch {
      setError('We could not flag this just now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyEscalated) {
    return (
      <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
        This conversation has been flagged for the Melio team.
      </p>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="text-sm underline underline-offset-4"
        style={{ color: 'var(--melio-text-muted)', opacity: disabled ? 0.5 : 1 }}
      >
        Send this to the Melio team
      </button>

      <EventModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Send this to the Melio team"
        actions={(
          <>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </>
        )}
      >
        <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-ink)' }}>
          This flags the whole conversation so the Melio team looks at it. They review
          these during working hours — it is not a live chat, so if you need your care
          team directly, use Messages.
        </p>

        <label className="block text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
          Anything you want to add? (optional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full rounded-2xl px-3 py-2 text-sm outline-none resize-none"
          style={{ border: '1px solid var(--melio-border)', color: 'var(--melio-ink)' }}
        />

        {error && <p className="text-sm mt-2" role="alert" style={{ color: 'var(--melio-terracotta-deep)' }}>{error}</p>}
      </EventModal>
    </>
  );
}
